import React, { useMemo, useCallback } from 'react';
import { Box, Paper, IconButton, Button } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import UnitPageShell from '../../components/shared/UnitPageShell';
import EditableTextBlock from '../../components/shared/EditableTextBlock';
import ZoomableImage from '../../components/shared/ZoomableImage';
import { useAuth } from '../../context/AuthContext';
import { useTTS } from '../../hooks/useTTS';
import { VolumeUp as SpeakerIcon } from '@mui/icons-material';
import { useTheme } from '../../contexts/ThemeContext';
import { tokens, getCardSx } from '../../theme/tokens';
import { updateUnitContent, uploadUnitAsset, useUnitOverride } from '../../services/unitOverridesService';

const PAGE_ID = 'm4_u3_x1_beleuchtung';

const TEXT_BLOCKS = [
  { key: 'block_titel', heading: 'MSR Funktionsbeschreibung: Raumbeleuchtungsregelung', body: '' },
  { key: 'block_intro', heading: 'Einführung', body: 'Die Beleuchtungsregelung in modernen Gebäuden geht weit über einfaches Ein- und Ausschalten hinaus. Ziel ist es, den Sehkomfort zu maximieren, die Produktivität zu steigern und gleichzeitig den Energieverbrauch drastisch zu reduzieren. Diese Beschreibung erläutert die MSR-Funktionen für die Raumbeleuchtung, von konventionellen Methoden bis hin zu intelligenten, sensorgesteuerten Systemen, unter besonderer Berücksichtigung der Energieeffizienz.' },
  { key: 'block_funktionen', heading: 'Grundlegende Funktionen', body: '1. Konventionelles Ein-/Ausschalten: Manuelles Schalten – einfach, geringe Effizienz.\n2. Helligkeitsregelung (Dimmen): Anpassung der Lichtintensität, Komfort & Energieeinsparung.\n3. Lichtszenenmanagement: Vordefinierte Einstellungen für Nutzungsszenen.\n4. Zeitgesteuerte Regelung: Automatisches Schalten / Dimmen nach Zeitprofilen.\n5. Human Centric Lighting (HCL): Dynamische Anpassung von Lichtfarbe & Intensität für circadiane Unterstützung.' },
  { key: 'block_leuchtmittel', heading: 'Leuchten & Leuchtmittel', body: '• Konventionelle Leuchten\n• Dimmbare Leuchten (LED, EVG)\n• Intelligente Leuchten (DALI, KNX, Funk)' },
  { key: 'block_sensoren', heading: 'Sensoren', body: '• Präsenzmelder (PIR/HF): Anwesenheitserkennung für bedarfsgerechtes Licht.\n• Helligkeitssensoren: Tageslichtmessung zur Konstantlichtregelung.' },
  { key: 'block_bedien', heading: 'Bediengeräte', body: '• Schalter/Taster für Ein/Aus & Dimmen\n• Smarte Taster/Panels (KNX, DALI) für Szenen & zentrale Steuerung' },
  { key: 'block_aktoren', heading: 'Aktoren & Steuerungen', body: '• Schaltaktoren\n• Dimmaktoren (Phasenan-/abschnitt, 1–10V, DALI, DMX)\n• Gateways/Controller (DALI-Controller, KNX-IP)\n• Gebäudeautomation (DDC-GA) für übergeordnete Logik' },
  { key: 'block_strategien', heading: 'Intelligente Strategien', body: '• Präsenzabhängige Regelung – Licht nur bei Bedarf\n• Tageslichtabhängige Konstantlichtregelung – Ergänzung statt Vollbetrieb\n• Zeitprofile – Reduktion außerhalb Nutzungszeiten\n• Szenensteuerung – Optimierte Komfortnutzung\n• HCL – Unterstützung circadianer Zyklen' },
  { key: 'block_bussysteme', heading: 'Bussysteme', body: '• DALI – Feingranulare Leuchtensteuerung\n• KNX – Gewerkeübergreifende Integration\n• EnOcean / Zigbee / Bluetooth Mesh – Funkbasierte Nachrüstung' },
  { key: 'block_parameter', heading: 'Wichtige Parameter', body: '• Soll-Helligkeit (Lux)\n• Nachlaufzeiten Präsenzmelder\n• Dimmkurven & Geschwindigkeiten\n• Schaltschwellen Helligkeitssensor\n• Zeitpläne & Szenenkonfiguration\n• Gruppen & Adressen (Bus)' },
  { key: 'block_effizienz', heading: 'Fokus Energieeffizienz', body: 'LED-Technologie, Präsenzmelder, Tageslichtnutzung, Zonenbildung, bedarfsgerechtes Dimmen, regelmäßige Wartung & Nutzerakzeptanz.' },
  { key: 'block_zusammenfassung', heading: 'Zusammenfassung', body: 'Moderne Beleuchtungsregelung steigert Komfort und senkt Energieverbrauch durch Sensorik, Aktoren, Szenen und integrative Bussysteme. Konsequente Parametrierung maximiert Nutzen.' }
];

const IMAGE_BLOCKS = [
  { key: 'img_0', src: '/assets/drawable/zzz_help.png', label: 'Beleuchtungsregelung (Platzhalter)' }
];

const M4_U3_X1_Beleuchtung: React.FC = () => {
  const navigate = useNavigate();
  const { token, canEditTextsM4, canUploadImagesM4 } = useAuth();
  const canEditTexts = canEditTextsM4;
  const canUploadImages = canUploadImagesM4;
  const { darkMode } = useTheme();
  const { override, refresh } = useUnitOverride(PAGE_ID);

  const imgSrc = useCallback((key: string, fallback: string) => {
    const v = override?.images?.[key];
    if (!v) return fallback;
    let s = String(v).trim();
    if (s.includes('/')) {
      s = s.split('/').pop() || s;
    }
    return `/assets/unit_custom/${s}`;
  }, [override]);

  const textBlocks = useMemo(() =>
    TEXT_BLOCKS.map(tb => ({ ...tb, body: override?.texts?.[tb.key] ?? tb.body })),
    [override]
  );

  const images = useMemo(() =>
    IMAGE_BLOCKS.map(img => ({ ...img, src: imgSrc(img.key, img.src) })),
    [imgSrc]
  );

  const fullText = useMemo(() => textBlocks.map(b => `${b.heading}. ${b.body}`).join(' '), [textBlocks]);
  const { speak, stop, speaking } = useTTS({ text: fullText });

  const handleTextSave = useCallback(async (blockKey: string, newBody: string) => {
    if (!token) return alert('Kein Token – bitte einloggen.');
    if (!canEditTexts) return alert('Keine Berechtigung zum Texte bearbeiten.');
    try {
      await updateUnitContent(PAGE_ID, { texts: { [blockKey]: newBody } }, token);
      await refresh();
    } catch (e: any) {
      console.error(e);
      alert('Fehler beim Speichern: ' + (e?.message || e));
    }
  }, [token, refresh, canEditTexts]);

  const handleImageReplace = useCallback(async (imageKey: string, file: File) => {
    if (!token) return alert('Kein Token – bitte einloggen.');
    if (!canUploadImages) return alert('Keine Berechtigung fuer Bilder-Uploads.');
    try {
      await uploadUnitAsset(PAGE_ID, imageKey, file, token);
      await refresh();
    } catch (e: any) {
      console.error(e);
      alert('Upload fehlgeschlagen: ' + (e?.message || e));
    }
  }, [token, refresh, canUploadImages]);

  const ttsBtn = React.createElement(IconButton, { onClick: speaking ? stop : () => speak(), sx: { color: darkMode ? tokens.colors.dark.text : tokens.colors.light.text } }, React.createElement(SpeakerIcon, { style: { fontSize: 28 } }));

  return React.createElement(UnitPageShell, { title: '4.3.1 Beleuchtung', actions: ttsBtn },
    React.createElement(Paper, { sx: getCardSx(darkMode), elevation: 2 },
      React.createElement(Box, { sx: { display: 'grid', gridTemplateColumns: { xs: '1fr', md: '5fr 7fr' }, gap: 3 } },
        React.createElement(Box, { sx: { display: 'flex', flexDirection: 'column', gap: 2 } },
          images.map(img => React.createElement(ZoomableImage, { key: img.key, src: img.src, label: img.label, onReplace: canUploadImages ? (f: File) => handleImageReplace(img.key, f) : undefined }))
        ),
        React.createElement(Box, { sx: { display: 'flex', flexDirection: 'column', gap: 2 } },
          textBlocks.map(tb => React.createElement(EditableTextBlock, { key: tb.key, heading: tb.heading, body: tb.body, onSave: canEditTexts ? (v: string) => handleTextSave(tb.key, v) : undefined, pageId: PAGE_ID, blockId: tb.key }))
        )
      )
    ),
    React.createElement(Box, { sx: { display: 'flex', gap: 1, mt: 2 } },
      React.createElement(Button, { variant: 'outlined', onClick: () => navigate('/units/m4_u3_x99_quiz') }, 'Zum Quiz')
    )
  );
};

export default M4_U3_X1_Beleuchtung;

